import { useEffect, useRef, useState } from "react";

interface Flight {
  id: string;
  text: string;
  from: { x: number; y: number };
  to: { x: number; y: number };
  startedAt: number;
  /** Target cluster id — surfaced via onFlightLand so the cluster card can pulse. */
  clusterId: string;
}

interface Props {
  /**
   * Array of pending flights. Each flight should be added once; FlightLayer
   * removes it from internal state after the animation completes (~900ms).
   */
  flights: Flight[];
  /** Notifies the parent that this flight finished so it can be removed from props. */
  onFlightDone: (id: string) => void;
  /**
   * Fires once per flight, at the moment the response card touches the target
   * cluster card (end of the flight phase, before the fade). Use this to pulse
   * the receiving cluster card.
   */
  onFlightLand?: (clusterId: string, flightId: string) => void;
}

// Hold the chip at the `from` position (the IncomingLane area in Stage Calm)
// for HOLD_MS before starting the flight. Gives the room time to read the
// new submission and identify it as theirs BEFORE it moves — otherwise the
// chip is in motion the moment it appears and the text is unreadable.
const HOLD_MS = 2000;
const FLIGHT_MS = 700;
const FADE_MS = 200;

/**
 * Renders absolutely-positioned response cards that animate from `from` to
 * `to` along a quadratic Bézier curve, then fade out. Lives on top of the
 * whole stage view (z-50, pointer-events-none).
 *
 * The parent (StageView) computes the source coordinates from the ResponseTicker
 * card's bounding rect and the target coordinates from the cluster card's
 * bounding rect (looked up by cluster_id). When a response is placed into a
 * cluster, StageView pushes a new flight; this component plays it.
 */
export default function FlightLayer({ flights, onFlightDone, onFlightLand }: Props) {
  const [now, setNow] = useState<number>(Date.now());
  const rafRef = useRef<number | null>(null);
  // Track which flights have already fired their landing callback so we don't
  // repeatedly pulse the cluster card every frame between FLIGHT_MS and
  // FLIGHT_MS + FADE_MS.
  const landedRef = useRef<Set<string>>(new Set());
  const hasFlights = flights.length > 0;

  // Idle the RAF loop when there are no flights — on a projector that's
  // 60 set-state-per-second savings the rest of the stage from re-rendering
  // for no reason. Restarts as soon as a flight arrives.
  useEffect(() => {
    if (!hasFlights) return;
    const tick = () => {
      setNow(Date.now());
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [hasFlights]);

  useEffect(() => {
    for (const f of flights) {
      const age = now - f.startedAt;
      // The "landed" moment is HOLD_MS + FLIGHT_MS into the timeline —
      // i.e., the chip has finished sitting at the lane and then
      // travelled to the cluster card.
      if (age >= HOLD_MS + FLIGHT_MS && !landedRef.current.has(f.id)) {
        landedRef.current.add(f.id);
        onFlightLand?.(f.clusterId, f.id);
      }
      if (age >= HOLD_MS + FLIGHT_MS + FADE_MS) {
        onFlightDone(f.id);
        landedRef.current.delete(f.id);
      }
    }
  }, [flights, now, onFlightDone, onFlightLand]);

  // Stack offset for chips currently in the hold phase. New submissions pile
  // up at the lane position; each subsequent chip sits STACK_OFFSET_PX below
  // the previous one so the room sees them as an ordered queue (the freshest
  // at the top) waiting to be clustered. Once a chip's hold ends and it flies
  // away, the remaining holding chips shift up — that motion itself reads as
  // "the queue is moving."
  const STACK_OFFSET_PX = 64;

  // Determine each flight's stack index: only flights currently in the hold
  // phase claim a slot, sorted by startedAt so newer chips sit below older
  // ones. (The oldest hold chip is at the top of the stack; it leaves first
  // when its hold expires.)
  const stackIndexById = new Map<string, number>();
  const holdingSorted = flights
    .filter((f) => now - f.startedAt < HOLD_MS)
    .sort((a, b) => a.startedAt - b.startedAt);
  holdingSorted.forEach((f, i) => stackIndexById.set(f.id, i));

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {flights.map((f) => {
        const age = Math.max(0, now - f.startedAt);
        // Three-phase timeline:
        //   1. age < HOLD_MS              : chip sits stationary at stacked from
        //   2. HOLD_MS .. HOLD_MS+FLIGHT_MS: chip flies along Bézier to `to`
        //   3. after FLIGHT_MS            : chip fades out at `to`
        const isHolding = age < HOLD_MS;
        const flightAge = Math.max(0, age - HOLD_MS);
        const linearT = Math.min(1, flightAge / FLIGHT_MS);
        // ease-in-out (sinusoidal) — steady acceleration, max speed mid-flight,
        // decelerates into landing. The prior cubic-out felt like the card
        // "paused" near the apex because cubic-out front-loaded the slow part.
        const t = isHolding ? 0 : 0.5 - 0.5 * Math.cos(Math.PI * linearT);

        // Stacked from-position: when holding, this chip claims slot N in the
        // queue and sits at lane-top + N * STACK_OFFSET. Once it starts flying
        // it leaves the stack (no longer in `holdingSorted`) so other chips
        // shift up. Non-holding chips just use the raw `from` (computed
        // upstream as the lane centre) — they're already on their way out.
        const stackIndex = stackIndexById.get(f.id);
        const stackedFromY =
          stackIndex !== undefined
            ? f.from.y + stackIndex * STACK_OFFSET_PX
            : f.from.y;
        const stackedFromX = f.from.x;

        // Gentler quadratic Bézier: apex lifted by 10% of horizontal distance
        // (down from 25%) so the path arcs but doesn't lob. Combined with the
        // ease-in-out timing the motion reads as a confident throw, not a hop.
        const cx = (stackedFromX + f.to.x) / 2;
        const cy =
          Math.min(stackedFromY, f.to.y) - Math.abs(f.to.x - stackedFromX) * 0.1;
        const x =
          (1 - t) * (1 - t) * stackedFromX +
          2 * (1 - t) * t * cx +
          t * t * f.to.x;
        const y =
          (1 - t) * (1 - t) * stackedFromY +
          2 * (1 - t) * t * cy +
          t * t * f.to.y;
        // Shrink-into-target: scale from 1.0 → 0.55 over the flight, enough
        // to suggest absorption but not so aggressive that the chip becomes
        // unreadable mid-flight. While holding, the chip stays full size.
        const scale = isHolding ? 1 : 1 - 0.45 * t;
        // Fade only kicks in AFTER the flight portion is complete.
        const fadeT =
          flightAge <= FLIGHT_MS ? 1 : Math.max(0, 1 - (flightAge - FLIGHT_MS) / FADE_MS);
        const opacity = flightAge < FLIGHT_MS ? 1 : fadeT;
        return (
          <div
            key={f.id}
            className="absolute rounded-rc-lg bg-rc-berry text-rc-paper px-5 py-3 text-[18px] font-semibold leading-[1.25] shadow-hard whitespace-nowrap overflow-hidden text-ellipsis transition-[top,left] duration-500 ease-out"
            style={{
              left: x,
              top: y,
              opacity,
              transform: `translate(-50%, -50%) scale(${scale})`,
              maxWidth: "420px",
            }}
          >
            {f.text}
          </div>
        );
      })}
    </div>
  );
}
